"use client";

import { motion, AnimatePresence } from "framer-motion"; // eslint-disable-line no-unused-vars
import { Mic, Paperclip, Send, StopCircle, X, FileText, Image, AlertCircle } from "lucide-react";
import {
  useEffect,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
  useCallback,
} from "react";
import { toast } from "react-toastify";
import { Tooltip } from "./ui/Tooltip";
import { WEBSOCKET_URL } from "../../config";

/** Max characters accumulated from voice before auto-stop */
const VOICE_TRANSCRIPT_CHAR_LIMIT = 16000;
/** Final transcript accepted only if mic RMS exceeded threshold within this window (ms) */
const VOICE_RECENT_ACTIVITY_MS = 1200;

function looksLikeSpeech(transcript) {
  const t = String(transcript ?? "").trim();
  if (t.length < 2) return false;
  // Reject obvious non-speech / punctuation-only scraps
  return /[a-zA-Z0-9\u00C0-\u024F\u0400-\u04FF]/.test(t);
}

const ChatInput = forwardRef(({ onSend, disabled = false, selectedChatId, setMessageChatId }, ref) => {

  const [message, setMessage] = useState("");
  const [isRecording, setIsRecording] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [error, setError] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState("disconnected"); // disconnected, connecting, connected

  // WebSocket + audio refs
  const wsRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const transcriptRef = useRef("");
  const dragCounterRef = useRef(0);
  const audioCtxRef = useRef(null);
  const vadIntervalRef = useRef(null);
  const lastVoiceActivityAtRef = useRef(0);

  // File preview states
  const [previewFile, setPreviewFile] = useState(null);
  const [previewType, setPreviewType] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  const inputRef = useRef(null);

  useEffect(() => {
    setPreviewFile(null)
    setMessage('')
  }, [selectedChatId])



  // Sync transcriptRef with text
  useEffect(() => {
    if (!isRecording) transcriptRef.current = message || "";
  }, [message, isRecording]);

  const stopRecording = useCallback(async () => {
    if (vadIntervalRef.current != null) {
      clearInterval(vadIntervalRef.current);
      vadIntervalRef.current = null;
    }
    const ctx = audioCtxRef.current;
    audioCtxRef.current = null;
    if (ctx && ctx.state !== "closed") {
      void ctx.close();
    }

    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }

    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send("stop");
      wsRef.current.close();
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
    }

    wsRef.current = null;
    mediaRecorderRef.current = null;
    streamRef.current = null;
    setIsRecording(false);
    setIsListening(false);
    setConnectionStatus("disconnected");
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      void stopRecording();
    };
  }, [stopRecording]);

  // 🧠 Start voice recording (WebSocket streaming)
  const startRecording = async () => {
    try {
      setError(null);
      setConnectionStatus("connecting");

      transcriptRef.current = message || "";
      lastVoiceActivityAtRef.current = 0;

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, sampleRate: 16000 },
      });
      streamRef.current = stream;

      // Simple voice activity detection (VAD) using mic level.
      try {
        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        if (AudioCtx) {
          const ctx = new AudioCtx();
          audioCtxRef.current = ctx;
          void ctx.resume();
          const source = ctx.createMediaStreamSource(stream);
          const analyser = ctx.createAnalyser();
          analyser.fftSize = 2048;
          source.connect(analyser);
          const data = new Uint8Array(analyser.fftSize);
          const threshold = 0.015;
          vadIntervalRef.current = window.setInterval(() => {
            analyser.getByteTimeDomainData(data);
            let sumSq = 0;
            for (let i = 0; i < data.length; i++) {
              const v = (data[i] - 128) / 128;
              sumSq += v * v;
            }
            const rms = Math.sqrt(sumSq / data.length);
            if (rms > threshold) {
              lastVoiceActivityAtRef.current = Date.now();
            }
          }, 120);
        }
      } catch {
        // If VAD fails, we still run recording; transcripts may be noisier.
      }

      const ws = new WebSocket(WEBSOCKET_URL);
      wsRef.current = ws;

      ws.onopen = () => {
        setConnectionStatus("connected");
        setIsRecording(true);
        ws.send("start");

        const mediaRecorder = new MediaRecorder(stream, { mimeType: "audio/webm;codecs=opus" });
        mediaRecorderRef.current = mediaRecorder;

        mediaRecorder.ondataavailable = (event) => {
          if (event.data.size > 0 && ws.readyState === WebSocket.OPEN) {
            ws.send(event.data);
          }
        };

        mediaRecorder.start(100);
        setIsListening(true);
      };

      ws.onmessage = (event) => {
        if (typeof event.data !== "string") return;
        try {
          const data = JSON.parse(event.data);
          if (data.error) {
            setError(data.error);
            return;
          }

          if (!data.results?.length) return;

          const result = data.results[0];
          const transcript = result.alternatives?.[0]?.transcript;
          const text = typeof transcript === "string" ? transcript.trim() : "";
          if (!text) return;

          const isFinal =
            result.final === true ||
            result.isFinal === true ||
            result.is_final === true;
          if (!isFinal) return;

          const now = Date.now();
          const hadRecentVoice =
            now - (lastVoiceActivityAtRef.current || 0) < VOICE_RECENT_ACTIVITY_MS;
          if (!hadRecentVoice) return;
          if (!looksLikeSpeech(text)) return;

          const combined = (transcriptRef.current + text + " ").slice(
            0,
            VOICE_TRANSCRIPT_CHAR_LIMIT,
          );
          transcriptRef.current = combined;
          setMessage(combined.trimEnd());
          if (combined.length >= VOICE_TRANSCRIPT_CHAR_LIMIT) {
            void stopRecording();
            setError(
              `Character limit of ${VOICE_TRANSCRIPT_CHAR_LIMIT} reached. Recording stopped.`,
            );
          }
        } catch {
          // Ignore non-JSON messages; they are often interim/noise.
        }
      };

      ws.onerror = () => {
        setError("Connection error occurred");
        setConnectionStatus("disconnected");
      };

      ws.onclose = () => {
        if (vadIntervalRef.current != null) {
          clearInterval(vadIntervalRef.current);
          vadIntervalRef.current = null;
        }
        const ctx = audioCtxRef.current;
        audioCtxRef.current = null;
        if (ctx?.state !== "closed") void ctx.close();

        if (
          mediaRecorderRef.current &&
          mediaRecorderRef.current.state !== "inactive"
        ) {
          try {
            mediaRecorderRef.current.stop();
          } catch {
            /* ignore */
          }
        }
        mediaRecorderRef.current = null;
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((t) => t.stop());
          streamRef.current = null;
        }
        wsRef.current = null;
        setConnectionStatus("disconnected");
        setIsRecording(false);
        setIsListening(false);
      };
    } catch (err) {
      console.error("Error starting recording:", err);
      if (vadIntervalRef.current != null) {
        clearInterval(vadIntervalRef.current);
        vadIntervalRef.current = null;
      }
      const ac = audioCtxRef.current;
      audioCtxRef.current = null;
      if (ac?.state !== "closed") void ac.close();
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
      setError(
        err.name === "NotAllowedError"
          ? "Microphone access denied. Enable it in browser settings."
          : "Failed to start recording. Please try again.",
      );
      setConnectionStatus("disconnected");
      setIsRecording(false);
    }
  };

  const handleMicClick = () => {
    if (isRecording) stopRecording();
    else startRecording();
  };

  useImperativeHandle(ref, () => ({
    focus: () => inputRef.current?.focus(),
  }));

  const cancelPreview = useCallback(() => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewFile(null);
    setPreviewType(null);
    setPreviewUrl(null);
  }, [previewUrl]);

  // 📨 Send message or file
  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (previewFile && previewType) {
      cancelPreview();
      await onSend?.(previewFile, previewType);
      return;
    }

    if (message.trim()) {
      const msg = message;
      setMessage("");
      await onSend?.(msg, "text");
      if (inputRef.current) inputRef.current.style.height = "auto";
    }
  }, [previewFile, previewType, message, onSend, cancelPreview]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Enter" && previewFile && !e.shiftKey) {
        e.preventDefault();
        handleSubmit?.(e);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [previewFile, handleSubmit]);


  const handlePaste = async (e) => {
    if (disabled) return;
    const cd = e.clipboardData || window.clipboardData;
    if (!cd) return;

    const items = cd.items || [];
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.kind === "file" && item.type && item.type.startsWith("image/")) {
        const file = item.getAsFile();
        if (file) {
          e.preventDefault();
          handleFileSelection(file);
          return;
        }
      }
    }

    const files = cd.files || [];
    if (files.length > 0 && files[0].type && files[0].type.startsWith("image/")) {
      e.preventDefault();
      handleFileSelection(files[0]);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;

    dragCounterRef.current++;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;

    dragCounterRef.current--;
    if (dragCounterRef.current === 0) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;

    setIsDragging(false);
    dragCounterRef.current = 0;

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFileSelection(files[0]);
    }
  };
  // 🗂 File Handling (same as before)
  const handleFileSelection = (file) => {
    if (!file) return;

    let fileType;
    const mimeType = file.type;
    const fileName = file.name?.toLowerCase() || "";

    if (mimeType.startsWith("image/")) {
      fileType = "image";
      setPreviewUrl(URL.createObjectURL(file));
    } else if (mimeType === "application/pdf" || fileName.endsWith(".pdf")) {
      fileType = "pdf";
    } else if (mimeType.includes("word") || fileName.endsWith(".docx") || fileName.endsWith(".doc")) {
      fileType = "docx";
    } else if (mimeType === "text/csv" || mimeType.includes("excel") || fileName.endsWith(".csv")) {
      fileType = "csv";
    } else if (mimeType === "application/json" || fileName.endsWith(".json")) {
      fileType = "json";
    } else {
      toast.error("Only image, PDF, DOCX, CSV, and JSON files are supported");
      return;
    }

    setPreviewFile(file);
    setPreviewType(fileType);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error("File must be less than 5 MB");
      e.target.value = "";
      return;
    }

    setMessageChatId(selectedChatId._id)

    handleFileSelection(file);
    e.target.value = "";
  };

  const formatFileSize = (bytes) => (bytes < 1024 ? bytes + " B" : bytes < 1048576 ? (bytes / 1024).toFixed(1) + " KB" : (bytes / 1048576).toFixed(1) + " MB");
  const getFileIcon = () => {
    if (previewType === 'image') return <ImageIcon className="w-8 h-8" />;
    if (previewType === 'pdf') return <FileText className="w-8 h-8" />;
    return <FileText className="w-8 h-8" />;
  };

  const getFileLabel = () => {
    switch (previewType) {
      case 'image':
        return 'Image';
      case 'pdf':
        return 'PDF Document';
      case 'docx':
        return 'Document';
      case 'csv':
        return 'CSV File';
      default:
        return 'File';
    }
  };
  
  return (
    <div className="relative">
      {/* File Preview */}
      <AnimatePresence>
        {previewFile && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            className="mb-2 p-3 bg-white border border-gray-200 rounded-2xl shadow-sm"
          >
            <div className="flex items-start gap-3">
              {/* Preview Content */}
              <div className="flex-shrink-0">
                {previewType === 'image' && previewUrl ? (
                  <img
                    src={previewUrl}
                    alt="Preview"
                    className="w-16 h-16 object-cover rounded-lg"
                  />
                ) : (
                  <div className="w-16 h-16 bg-blue-50 rounded-lg flex items-center justify-center text-blue-500">
                    {getFileIcon()}
                  </div>
                )}
              </div>

              {/* File Info */}
              <div className="flex-grow min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {previewFile.name}
                </p>
                <p className="text-xs text-gray-500">
                  {formatFileSize(previewFile.size)}
                </p>
                <p className="text-xs text-gray-400">
                  {getFileLabel()}
                </p>
              </div>



              <div className="flex flex-col gap-3 items-end">

                {/* Cancel Button */}
                <button
                  onClick={cancelPreview}
                  className="flex-shrink-0 p-1 hover:bg-gray-100 rounded-full transition-colors"
                  type="button"
                >
                  <X className="w-4 h-4 text-gray-500" />
                </button>

                <div className="flex items-center gap-1">
                  <motion.button
                    onClick={handleSubmit}
                    className={`p-2 rounded-full transition-all duration-300 ${(message.trim() || previewFile) && !disabled
                      ? "bg-gradient-to-r from-primary to-secondary text-white"
                      : "bg-gray-700/50 text-gray-100 cursor-not-allowed"
                      }`}
                    disabled={(!message.trim() && !previewFile) || disabled}
                    whileHover={(message.trim() || previewFile) && !disabled ? { scale: 1.1 } : {}}
                    whileTap={(message.trim() || previewFile) && !disabled ? { scale: 0.9 } : {}}
                  >
                    <Send size={18} />
                  </motion.button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {!previewFile && <motion.form
        onSubmit={handleSubmit}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`relative  flex items-center p-1 bg-black/5 border rounded-3xl transition-all duration-300 ${isDragging
          ? 'border-blue-500 border-2 bg-blue-50'
          : 'border-gray-200 hover:bg-gray-100'
          }`}
        initial={{ y: 10, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.3 }}
      >
        {/* Drag overlay */}
        {isDragging && (
          <div className="absolute inset-0 bg-blue-500/10 backdrop-blur-sm z-10 flex items-center justify-center rounded-3xl">
            <div className="text-center flex gap-2 items-center">
              <Paperclip size={18} className="mx-auto text-blue-500" />
              <p className="text-sm font-medium text-blue-600">Drop file here</p>
            </div>
          </div>
        )}



        <div className="flex group relative items-center gap-1">

          <motion.label
            htmlFor="media"
            className={`p-2 rounded-full  transition-all duration-300 ${!disabled
              ? "bg-gradient-to-r from-primary to-secondary text-white cursor-pointer"
              : "bg-gray-700/50 text-gray-100 cursor-not-allowed"
              }`}

          >
            {!disabled && <input
              onChange={handleFileChange}
              type="file"
              className="hidden"
              accept="image/*,application/pdf,.pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.docx,application/msword,.doc,text/csv,.csv,application/vnd.ms-excel,application/json,.json"
              id="media"
            />}
            <Tooltip delay={3000} content={'only pdf, image, docx, csv and json file supported of max 5 mb.'}  >

              <div>

                <Paperclip size={18} />
              </div>
            </Tooltip>
          </motion.label>
        </div>
        {error && (
          <div className="absolute left-0 right-0 bottom-[110%] mt-2 flex items-start justify-between gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg z-10">
            <div className="flex items-center gap-2">

              <AlertCircle className="w-5 h-5 flex-shrink-0 " />
              <p className="text-sm">{error}</p>
            </div>
            <X size={18} className="cursor-pointer" onClick={() => setError(null)} />
          </div>
        )}
        {isRecording && (
          <div className="absolute right-3 -top-10 flex items-center gap-2 bg-white px-3 py-2 rounded-lg shadow-lg border border-gray-200 animate-slide-up z-10">
            <div className="flex gap-1">
              <span className="w-1 h-4 bg-red-500 rounded-full animate-bounce"></span>
              <span className="w-1 h-4 bg-red-500 rounded-full animate-bounce" style={{ animationDelay: "150ms" }}></span>
              <span className="w-1 h-4 bg-red-500 rounded-full animate-bounce" style={{ animationDelay: "300ms" }}></span>
            </div>
            <span className="text-sm font-medium text-gray-700">
              {isListening ? "Listening..." : connectionStatus === "connecting" ? "Connecting..." : "Processing..."}
            </span>
          </div>
        )}

        <div className="flex flex-grow items-center pl-2">
          <textarea
            ref={inputRef}
            rows={1}
            value={message}
            onChange={(e) => {
              setMessage(e.target.value);
              e.target.style.height = "auto";
              e.target.style.height = `${Math.min(e.target.scrollHeight, 50)}px`;
            }}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}

            placeholder="Ask me anything..."
            disabled={disabled}
            className="w-full bg-transparent border-none outline-none focus:outline-none focus:shadow-none appearance-none focus:ring-0 text-black placeholder-gray-400 py-3 px-2 text-sm resize-none max-h-[220px] min-h-[40px] custom-scrollbar"
          />
        </div>

        <div className="flex items-center gap-1 ml-2">
          <div className="flex items-center gap-1">
            <motion.button
              type="button"
              onClick={handleMicClick}
              disabled={disabled}
              className={`p-2 rounded-full border border-gray-300 ${isRecording ? "bg-red-500/20 text-red-500 animate-pulse" : "bg-white/10 hover:bg-white/20"}`}
              title={isRecording ? "Stop recording" : "Start voice input"}
            >
              {isRecording ? <StopCircle className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
            </motion.button>
          </div>

          <div className="flex items-center gap-1">
            <motion.button
              type="submit"
              className={`p-2 rounded-full transition-all duration-300 ${(message.trim() || previewFile) && !disabled
                ? "bg-gradient-to-r from-primary to-secondary text-white"
                : "bg-gray-700/50 text-gray-100 cursor-not-allowed"
                }`}
              disabled={(!message.trim() && !previewFile) || disabled}
              whileHover={(message.trim() || previewFile) && !disabled ? { scale: 1.1 } : {}}
              whileTap={(message.trim() || previewFile) && !disabled ? { scale: 0.9 } : {}}
            >
              <Send size={18} />
            </motion.button>
          </div>
        </div>
      </motion.form>}
    </div>
  );
});

ChatInput.displayName = "ChatInput";
export default ChatInput;
